import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ShoppingCart,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  FileText,
  TrendingUp,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';

const API_BASE = '/api';

interface DashboardStats {
  orders: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    ready: number;
    parked: number;
    byCustomer: { customer: string; orders: number }[];
    byDay: { date: string; day: string; orders: number }[];
  };
  hiring: {
    total: number;
    new: number;
    cultureSurveySent: number;
    interviewing: number;
    offersSent: number;
    byDay: { date: string; day: string; candidates: number }[];
  };
  recentActivity: {
    id: string;
    type: 'order' | 'hiring';
    message: string;
    time: string;
    status: 'success' | 'warning' | 'error' | 'info';
  }[];
}

interface SummaryCardProps {
  title: string;
  value: number | string;
  icon: React.ElementType;
  color: 'green' | 'yellow' | 'blue' | 'red';
  link?: string;
  loading?: boolean;
}

function SummaryCard({ title, value, icon: Icon, color, link, loading }: SummaryCardProps) {
  const colorClasses = {
    green: 'bg-perfectted-light text-perfectted-dark',
    yellow: 'bg-yellow-50 text-yellow-600',
    blue: 'bg-blue-50 text-blue-600',
    red: 'bg-red-50 text-red-600',
  };

  const Card = (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-card-hover transition-shadow cursor-pointer">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          {loading ? (
            <div className="h-9 w-16 bg-gray-200 animate-pulse rounded mt-2"></div>
          ) : (
            <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );

  return link ? <Link to={link}>{Card}</Link> : Card;
}

function ActivityItem({ item }: { item: DashboardStats['recentActivity'][0] }) {
  const statusColors = {
    success: 'bg-perfectted-light text-perfectted-dark',
    warning: 'bg-yellow-50 text-yellow-600',
    error: 'bg-red-50 text-red-600',
    info: 'bg-blue-50 text-blue-600',
  };

  const statusIcons = {
    success: CheckCircle,
    warning: AlertCircle,
    error: AlertCircle,
    info: FileText,
  };

  const Icon = statusIcons[item.status];

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className={`p-2 rounded-lg ${statusColors[item.status]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 truncate">{item.message}</p>
        <p className="text-xs text-gray-500 mt-0.5">{item.time}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  // Fetch dashboard stats from API
  const { data: stats, isLoading, refetch } = useQuery<{ data: DashboardStats }>({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/dashboard/stats`);
      if (!res.ok) {
        // Return default empty stats if endpoint doesn't exist yet
        return {
          data: {
            orders: { total: 0, pending: 0, approved: 0, rejected: 0, ready: 0, parked: 0, byCustomer: [], byDay: [] },
            hiring: { total: 0, new: 0, cultureSurveySent: 0, interviewing: 0, offersSent: 0, byDay: [] },
            recentActivity: [],
          }
        };
      }
      return res.json();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const dashboardStats = stats?.data || {
    orders: { total: 0, pending: 0, approved: 0, rejected: 0, ready: 0, parked: 0, byCustomer: [], byDay: [] },
    hiring: { total: 0, new: 0, cultureSurveySent: 0, interviewing: 0, offersSent: 0, byDay: [] },
    recentActivity: [],
  };

  // Combine orders and hiring data for weekly chart
  const weeklyData = dashboardStats.orders.byDay.map((day, idx) => ({
    day: day.day,
    orders: day.orders,
    hiring: dashboardStats.hiring.byDay[idx]?.candidates || 0,
  }));

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-gray-500 mt-1">Overview of orders and hiring automation</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Orders Summary */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Orders</h3>
          <Link to="/orders" className="text-perfectted-primary hover:text-perfectted-dark text-sm font-medium flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Orders"
            value={dashboardStats.orders.total}
            icon={ShoppingCart}
            color="blue"
            link="/orders"
            loading={isLoading}
          />
          <SummaryCard
            title="Ready"
            value={dashboardStats.orders.ready}
            icon={CheckCircle}
            color="green"
            link="/orders?status=ready"
            loading={isLoading}
          />
          <SummaryCard
            title="Parked"
            value={dashboardStats.orders.parked}
            icon={Clock}
            color="yellow"
            link="/orders?status=Parked"
            loading={isLoading}
          />
          <SummaryCard
            title="Pending"
            value={dashboardStats.orders.pending}
            icon={AlertCircle}
            color="red"
            link="/orders?status=pending"
            loading={isLoading}
          />
        </div>
      </div>

      {/* Hiring Summary */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Hiring</h3>
          <Link to="/hiring" className="text-perfectted-primary hover:text-perfectted-dark text-sm font-medium flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Candidates"
            value={dashboardStats.hiring.total}
            icon={Users}
            color="blue"
            link="/hiring"
            loading={isLoading}
          />
          <SummaryCard
            title="New Applicants"
            value={dashboardStats.hiring.new}
            icon={Users}
            color="yellow"
            link="/hiring?status=new"
            loading={isLoading}
          />
          <SummaryCard
            title="Culture Survey Sent"
            value={dashboardStats.hiring.cultureSurveySent}
            icon={FileText}
            color="blue"
            link="/hiring?status=culture_survey_sent"
            loading={isLoading}
          />
          <SummaryCard
            title="Offers Sent"
            value={dashboardStats.hiring.offersSent}
            icon={CheckCircle}
            color="green"
            link="/hiring?status=offer_sent"
            loading={isLoading}
          />
        </div>
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weekly Activity Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Activity</h3>
          {isLoading ? (
            <div className="h-64 bg-gray-100 animate-pulse rounded"></div>
          ) : weeklyData.length > 0 ? (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={weeklyData}>
                    <defs>
                      <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#40b86b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#40b86b" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorHiring" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="orders"
                      stroke="#40b86b"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorOrders)"
                      name="Orders"
                    />
                    <Area
                      type="monotone"
                      dataKey="hiring"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorHiring)"
                      name="Hiring"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-perfectted-primary"></div>
                  <span className="text-sm text-gray-600">Orders</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-gray-600">Hiring</span>
                </div>
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No activity data available
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          {isLoading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-12 bg-gray-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : dashboardStats.recentActivity.length > 0 ? (
            <div className="space-y-1">
              {dashboardStats.recentActivity.map((item) => (
                <ActivityItem key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              No recent activity
            </div>
          )}
        </div>
      </div>

      {/* Orders by Customer Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Orders by Customer (This Week)</h3>
        {isLoading ? (
          <div className="h-64 bg-gray-100 animate-pulse rounded"></div>
        ) : dashboardStats.orders.byCustomer.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dashboardStats.orders.byCustomer.slice(0, 10)}
                layout="vertical"
                margin={{ left: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" stroke="#6b7280" fontSize={12} />
                <YAxis dataKey="customer" type="category" stroke="#6b7280" fontSize={12} width={100} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="orders" fill="#40b86b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            No customer data available
          </div>
        )}
      </div>
    </div>
  );
}
